import React, { useEffect, useState, useCallback } from 'react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { Select } from '@/components/ui/Select/Select';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { Progress } from '@/components/ui/Progress/Progress';
import { CreditService, type CreditSummary, type CreditTransaction } from '@/services/CreditService';
import { CreditsPaywallCard } from '@/components/Paywall/CreditsPaywallCard';
import { useOrgStore } from '@/stores/orgStore';

const svc = new CreditService();

const formatNum = (v: unknown) => Number(v || 0).toLocaleString();
const formatDate = (d: string) => new Date(d).toLocaleString();

/** Human-readable relative expiry: "in 3 days", "in 2 months", "today" */
const relativeExpiry = (d: string) => {
  const now = new Date();
  const exp = new Date(d);
  const diffMs = exp.getTime() - now.getTime();
  if (diffMs <= 0) return 'Expired';
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 30) return `in ${days} days`;
  const months = Math.round(days / 30);
  return months === 1 ? 'in 1 month' : `in ${months} months`;
};

const typeBadge = (t: CreditTransaction['type']) => {
  switch (t) {
    case 'grant':  return <Badge variant="success">Grant</Badge>;
    case 'debit':  return <Badge variant="secondary">Usage</Badge>;
    case 'refund': return <Badge variant="default">Refund</Badge>;
    case 'expiry': return <Badge variant="warning">Expired</Badge>;
  }
};

export function Credits() {
  const orgId = useOrgStore((s) => s.activeOrgId);
  const [summary, setSummary] = useState<CreditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [txPage, setTxPage] = useState(0);
  const [txType, setTxType] = useState<string>('');
  const [txItems, setTxItems] = useState<CreditTransaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const PAGE_SIZE = 20;

  useEffect(() => {
    setLoading(true);
    setSummary(null);
    svc.getSummary()
      .then(setSummary)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  const loadTx = useCallback(async () => {
    setTxLoading(true);
    try {
      const res = await svc.listTransactions({ limit: PAGE_SIZE, offset: txPage * PAGE_SIZE, type: txType || undefined });
      setTxItems(res.items);
      setTxTotal(res.total);
    } catch {}
    setTxLoading(false);
  }, [txPage, txType, orgId]);

  useEffect(() => { loadTx(); }, [loadTx]);

  if (loading) return <PageContainer><div className="flex justify-center py-12"><Spinner size="lg" /></div></PageContainer>;

  const bal = summary?.balance;

  // Compute total credits across active grants (original + remaining)
  const totalOriginal = summary?.activeGrants?.reduce((s, g) => s + Number(g.amount_credits), 0) ?? 0;
  const totalUsed = totalOriginal - Number(bal?.balanceCredits ?? 0);

  return (
    <PageContainer>
      <PageHeader title="Credits & Usage" description="View your credit balance, active grants, and usage history" />

      {/* Paywall Card — shown when credits exhausted */}
      <div className="mt-6">
        <CreditsPaywallCard />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Available Credits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatNum(bal?.balanceCredits)}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {totalOriginal > 0
                ? `${formatNum(totalUsed > 0 ? totalUsed : 0)} used of ${formatNum(totalOriginal)}`
                : 'No active grants'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Grants</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summary?.activeGrants?.length ?? 0}</div>
            <div className="text-sm text-muted-foreground mt-1">currently active</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Nearest Expiry</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium">
              {bal?.nearestExpiry ? relativeExpiry(bal.nearestExpiry) : '—'}
            </div>
            {bal?.nearestExpiry && (
              <div className="text-sm text-muted-foreground mt-1">
                {new Date(bal.nearestExpiry).toLocaleDateString()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Active Grants */}
      {summary?.activeGrants && summary.activeGrants.length > 0 && (
        <Card className="mt-6">
          <CardHeader><CardTitle>Active Grants</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4">Description</th>
                    <th className="py-2 pr-4 w-56">Usage</th>
                    <th className="py-2">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.activeGrants.map(g => {
                    const remaining = Number(g.remaining_credits);
                    const original = Number(g.amount_credits);
                    const used = original - remaining;
                    const pct = original > 0 ? (remaining / original) * 100 : 0;
                    const variant = pct > 25 ? 'default' : pct > 10 ? 'warning' : 'error';

                    return (
                      <tr key={g.id} className="border-b last:border-0">
                        <td className="py-3 pr-4">{g.description || '—'}</td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium tabular-nums whitespace-nowrap">
                              {formatNum(remaining)}
                            </span>
                            <span className="text-muted-foreground whitespace-nowrap">
                              / {formatNum(original)}
                            </span>
                          </div>
                          <Progress value={remaining} max={original} size="sm" variant={variant} className="mt-1" />
                        </td>
                        <td className="py-3 whitespace-nowrap">
                          <span>{relativeExpiry(g.expires_at)}</span>
                          <span className="text-muted-foreground ml-2 text-xs">
                            ({new Date(g.expires_at).toLocaleDateString()})
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Transaction History</CardTitle>
            <div className="w-40">
              <Select
                value={txType}
                onChange={(e) => { setTxType(e.target.value); setTxPage(0); }}
              >
                <option value="">All types</option>
                <option value="grant">Grants</option>
                <option value="debit">Usage</option>
                <option value="refund">Refunds</option>
                <option value="expiry">Expiries</option>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {txLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : txItems.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No transactions yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">Credits</th>
                      <th className="py-2 pr-4">Description</th>
                      <th className="py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txItems.map(tx => (
                      <tr key={tx.id} className="border-b last:border-0">
                        <td className="py-2 pr-4">{typeBadge(tx.type)}</td>
                        <td className="py-2 pr-4 font-medium tabular-nums">
                          {tx.type === 'debit' ? '−' : '+'}{formatNum(tx.amount_credits)}
                        </td>
                        <td className="py-2 pr-4">{tx.description || '—'}</td>
                        <td className="py-2 whitespace-nowrap">{formatDate(tx.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {txTotal > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">
                    Showing {txPage * PAGE_SIZE + 1}–{Math.min((txPage + 1) * PAGE_SIZE, txTotal)} of {txTotal}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={txPage === 0} onClick={() => setTxPage(p => p - 1)}>Previous</Button>
                    <Button size="sm" variant="outline" disabled={(txPage + 1) * PAGE_SIZE >= txTotal} onClick={() => setTxPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
